import { ApiErrorDTO, getGetCurrentUserQueryKey, useSetTermsAndConditionsConsent } from "@bvhi/backend-api/apis";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LoadingButton } from "@bvhi/ui-base";
import { UpdateIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useRef, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, date, object } from "yup";
import { FormCheckboxTermsOfService } from "../components/common/form-checkbox-field-terms-of-service";
import { useKeyboardShortcut } from "../hooks/use-keyboard-shortcuts";
import { useToast } from "../hooks/use-toast";
import { DialogError } from "./dialog-error";

interface Props {
  open: boolean;
}

interface FormValues {
  acceptedTermsOfService: Dayjs;
}

export const AcceptTermsOfServiceDialog = ({ open }: Props) => {
  const { t } = useTranslation(["dialogs", "validation", "toasts"]);
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<ApiErrorDTO>();
  const { pushCustomErrorToast, pushCustomSuccessToast } = useToast();

  const schema: AnyObjectSchema = object({
    acceptedTermsOfService: date().required(t("validation:required")),
  });

  const { mutate: setTermsAndConditionsConsent, isPending: isSetTermsAndConditionsConsentPending } = useSetTermsAndConditionsConsent();

  const {
    formState: { isDirty, isValid, ...otherFormState },
    handleSubmit,
    ...otherMethods
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      acceptedTermsOfService: undefined,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    setTermsAndConditionsConsent(
      {
        data: {
          termsAndConditionsConsent: data.acceptedTermsOfService.toISOString(),
        },
      },
      {
        onSuccess: () => {
          setApiError(undefined);
          pushCustomSuccessToast(t("toasts:toasts.terms.of.service.success"));
        },
        onError: (error) => {
          if (error.status && [400, 403, 409].includes(error.status)) {
            setApiError(error);
          }
          if (error.status === 500) pushCustomErrorToast(t("toasts:toasts.general.error"));
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetCurrentUserQueryKey() });
        },
      }
    );
  };

  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  useKeyboardShortcut({
    key: "Enter",
    onKeyPressed: () => primaryButtonRef.current?.click(),
  });

  return (
    <Dialog disableEscapeKeyDown open={open}>
      <DialogTitle>{t("dialogs:dialogs.accept.terms.of.service.dialog.title")}</DialogTitle>
      <FormProvider formState={{ isDirty, isValid, ...otherFormState }} handleSubmit={handleSubmit} {...otherMethods}>
        <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
          <DialogContent>
            <DialogContentText>{t("dialogs:dialogs.accept.terms.of.service.dialog.body")}</DialogContentText>
            <FormCheckboxTermsOfService />
          </DialogContent>
          <DialogError color="error" error={apiError} />
          <DialogActions>
            <LoadingButton
              ref={primaryButtonRef}
              disabled={!isDirty}
              loading={isSetTermsAndConditionsConsentPending}
              startIcon={<UpdateIcon />}
              type="submit"
              variant="contained"
            >
              {t("dialogs:dialogs.accept.terms.of.service.dialog.submit")}
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
